import React, { useCallback } from 'react';

import Observer from '@researchgate/react-intersection-observer';
import PropTypes from 'prop-types';
import { FaFilter } from 'react-icons/fa';
import { Transition } from 'react-transition-group';
import styled, { css } from 'styled-components';

import {
  useSizeTransition,
  FilterDrawerButton as BaseFilterDrawerButton,
} from '~/techstyle-shared/react-components';

import { useFloatingFilterBarContext } from '../../context/FloatingFilterBarContext/FloatingFilterBarContext';
import SortDropdown from '../SortDropdown';
import StyleGuideFilterPills from '../StyleGuideFilterPills/StyleGuideFilterPills';

if (process.browser) {
  require('intersection-observer');
}

const FilterBarWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  transition: opacity 300ms ease-in-out;
  background-color: ${({ theme }) => theme.colors.white};
  opacity: 0;
`;

const FilterDrawerButton = styled(BaseFilterDrawerButton)`
  font-size: 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.fontStacks.textFamily};
  color: ${({ theme }) => theme.colors.textDefault};
  height: 16px;
  line-height: 1;
`;

const FilterDrawerButtonWrapper = styled.div`
  flex-grow: 1;
  text-align: right;
`;

const sortDropdownButtonStyles = css`
  background-color: ${({ theme }) => theme.colors.palette.white};
  height: 16px;
  margin-bottom: 8px;

  &[aria-expanded='true'] {
    background-color: ${({ theme }) => theme.colors.palette.white};
  }
`;

const SortDropdownWrapper = styled.div`
  flex-grow: 1;
  z-index: 3;
`;

const FlexRow = styled.div`
  flex-basis: 100%;
`;

const transitionStyles = {
  entering: { opacity: 1 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

function FilterBarObserver({ children }) {
  const context = useFloatingFilterBarContext();
  const { setFilterBarVisible } = context;

  const handleIntersection = useCallback(
    (entry) => {
      const { isIntersecting, boundingClientRect } = entry;
      if (boundingClientRect.width && boundingClientRect.height) {
        setFilterBarVisible(!isIntersecting);
      } else {
        setFilterBarVisible(false);
      }
    },
    [setFilterBarVisible]
  );

  const options = {
    root: process.browser && window.frameElement ? document : undefined,
    rootMargin: '0px 0px 8000px 0px',
  };

  return (
    <Observer onChange={handleIntersection} {...options}>
      {children}
    </Observer>
  );
}

FilterBarObserver.displayName = 'FloatingFilterButton.InlineButtonObserver';
FilterBarObserver.propTypes = { children: PropTypes.node };

const FloatingFilterBar = React.forwardRef(
  (
    {
      icon = <FaFilter />,
      filterCount: filterCountFromProps,
      isFilterBarVisible,
      transitionDuration = 300,
      ...rest
    },
    ref
  ) => {
    const context = useFloatingFilterBarContext();
    const { forceReflow } = useSizeTransition();

    if (isFilterBarVisible == null) {
      if (!context) {
        throw new Error(
          'FloatingFilterButton context not found, did you forget to add a <FloatingFilterButton.Provider>?'
        );
      }
      isFilterBarVisible = context.isFilterBarVisible;
    }

    return (
      <Transition
        mountOnEnter
        unmountOnExit
        in={isFilterBarVisible}
        timeout={transitionDuration}
        onEnter={forceReflow}
      >
        {(state) => (
          <FilterBarWrapper
            ref={ref}
            data-autotag="filter-header"
            data-transition-state={state}
            transitionDuration={transitionDuration}
            style={{
              ...transitionStyles[state],
            }}
          >
            <SortDropdownWrapper>
              <SortDropdown buttonStyle={sortDropdownButtonStyles} />
            </SortDropdownWrapper>
            <FilterDrawerButtonWrapper>
              <FilterDrawerButton />
            </FilterDrawerButtonWrapper>
            <FlexRow />
            <StyleGuideFilterPills />
          </FilterBarWrapper>
        )}
      </Transition>
    );
  }
);

FloatingFilterBar.displayName = 'FloatingFilterBar';
FloatingFilterBar.FilterBarObserver = FilterBarObserver;

FloatingFilterBar.propTypes = {
  /**
   * Number of filters currently applied
   */
  filterCount: PropTypes.number,
  /**
   * Icon to display on Filter Button
   */
  icon: PropTypes.node,
  /**
   * Override the context value to determine whether or not the filter bar is visible
   */
  isFilterBarVisible: PropTypes.bool,
  /**
   * How long the transition will last when the filter bar appears and disappears.
   */
  transitionDuration: PropTypes.number,
};

export default FloatingFilterBar;
